import { useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { SwitchTransition, TransitionGroup } from 'react-transition-group';
import { DedicatedEngine } from '@rossum/api-client/dedicatedEngines';
import {
  DedicatedEngineSchema,
  isSimpleMultivalueField,
} from '@rossum/api-client/dedicatedEngineSchema';
import { IconAlertTriangle } from '@rossum/ui/icons/tabler';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Fade,
  Grow,
  Stack,
  SvgIcon,
  Typography,
} from '@rossum/ui/material';
import { useMountTransition } from '../../../../../../../../utils/hooks/useMountTransition';
import { EngineStatusChip } from '../../../../../../components/EngineStatusChip';
import { useDedicatedEngineSchemaValidateResult } from '../../../../../../hooks/useDedicatedEngineSchemaValidateResult';
import { EngineSchemaObjectCard } from '../FieldsMappingStep/components/EngineSchemaObjectCard';
import { anyErrors, isErrorAtIndex } from './utils';

type IntegrityValidationStepProps = {
  engine: DedicatedEngine;
  engineSchema: DedicatedEngineSchema;
  onPrevious: () => void;
  onComplete: () => void;
  onError: (e?: unknown) => void;
};

const IntegrityValidationStep = ({
  engine,
  engineSchema,
  onComplete,
  onError,
  onPrevious,
}: IntegrityValidationStepProps) => {
  const intl = useIntl();

  const [transitionProps, transitionOut] = useMountTransition();

  const handlePreviousClick = useCallback(() => {
    transitionOut(onPrevious);
  }, [onPrevious, transitionOut]);

  const { data: integrityValidationData, status: integrityValidationStatus } =
    useDedicatedEngineSchemaValidateResult({
      content: engineSchema.content,
    });

  const showBanner =
    integrityValidationStatus === 'success' && integrityValidationData
      ? anyErrors(integrityValidationData)
        ? 'error'
        : 'success'
      : 'none';

  useEffect(() => {
    if (integrityValidationStatus === 'success' && integrityValidationData) {
      if (anyErrors(integrityValidationData)) {
        onError();
      } else {
        onComplete();
      }
    }
  }, [integrityValidationData, integrityValidationStatus, onComplete, onError]);

  return (
    <Stack spacing={3} divider={<Divider />}>
      <Fade {...transitionProps}>
        <Stack spacing={8} sx={{ pb: 8 }}>
          <Stack
            spacing={3}
            alignItems="center"
            sx={{ width: '100%', pl: 8, pr: 8 }}
          >
            <Stack spacing={2} alignItems="center">
              <Typography variant="h5">
                {intl.formatMessage({
                  id: 'components.integrityValidationStep.title',
                })}
              </Typography>
              <EngineStatusChip status={engine.status} />
              <Typography
                variant="body2"
                color="text.secondary"
                textAlign="center"
              >
                {intl.formatMessage({
                  id: 'components.integrityValidationStep.description',
                })}
              </Typography>
              {engine.status === 'draft' && (
                <SwitchTransition>
                  <Grow key={showBanner} appear unmountOnExit>
                    {showBanner === 'error' ? (
                      <Alert
                        variant="filled"
                        severity="error"
                        sx={{ width: '100%' }}
                        icon={
                          <SvgIcon fontSize="large">
                            <IconAlertTriangle />
                          </SvgIcon>
                        }
                      >
                        <Typography variant="body2">
                          {intl.formatMessage({
                            id: 'components.integrityValidationStep.errorMessage',
                          })}
                        </Typography>
                      </Alert>
                    ) : showBanner === 'success' ? (
                      <Alert
                        variant="filled"
                        severity="success"
                        sx={{ width: '100%' }}
                      >
                        <Typography variant="body2">
                          {intl.formatMessage({
                            id: 'components.integrityValidationStep.successMessage',
                          })}
                        </Typography>
                      </Alert>
                    ) : (
                      <span />
                    )}
                  </Grow>
                </SwitchTransition>
              )}
            </Stack>
            {integrityValidationStatus === 'loading' ? (
              <CircularProgress />
            ) : integrityValidationStatus === 'error' ? (
              <Stack spacing={2} sx={{ width: '100%' }}>
                <Grow in key="validate-error" appear unmountOnExit>
                  <Alert
                    severity="error"
                    variant="filled"
                    icon={
                      <SvgIcon fontSize="large">
                        <IconAlertTriangle />
                      </SvgIcon>
                    }
                    sx={{ width: '100%' }}
                  >
                    {intl.formatMessage({
                      id: 'components.integrityValidationStep.fetchingError',
                    })}
                  </Alert>
                </Grow>
              </Stack>
            ) : (
              <Stack spacing={2} sx={{ width: '100%' }}>
                {/* eslint-disable-next-line no-restricted-syntax -- old code */}
                <TransitionGroup style={{ width: '100%' }} component={null}>
                  {engineSchema.content.fields.map((field, i) =>
                    integrityValidationData &&
                    isErrorAtIndex(integrityValidationData, i) ? (
                      <Grow
                        // oxlint-disable-next-line react/no-array-index-key -- oxlint stricter than eslint — index used as fallback, not sole key
                        key={
                          isSimpleMultivalueField(field)
                            ? `${i}-${field.children.engineOutputId}`
                            : `${i}-${field.engineOutputId}`
                        }
                      >
                        <Box sx={{ width: '100%' }}>
                          <EngineSchemaObjectCard
                            field={field}
                            fieldErrors={
                              integrityValidationData.content?.fields?.[i]
                            }
                            fieldType="headerField"
                            actions={[]}
                          />
                        </Box>
                      </Grow>
                    ) : null
                  )}
                </TransitionGroup>
                {showBanner === 'error' && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    textAlign="start"
                  >
                    {intl.formatMessage({
                      id: 'components.integrityValidationStep.errorsDescription',
                    })}
                  </Typography>
                )}
              </Stack>
            )}
          </Stack>
          {engine.status === 'draft' && (
            <Stack spacing={2} alignItems="center">
              <Typography variant="h5">
                {intl.formatMessage({
                  id: 'components.integrityValidationStep.readyTitle',
                })}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {intl.formatMessage({
                  id: 'components.integrityValidationStep.readyDescription',
                })}
              </Typography>
            </Stack>
          )}
        </Stack>
      </Fade>
      <Stack
        direction="row"
        spacing={2}
        sx={{ p: 4, pt: 1 }}
        justifyContent="space-between"
      >
        <Button
          variant="outlined"
          color="secondary"
          onClick={handlePreviousClick}
        >
          {intl.formatMessage({ id: 'components.stepWorkflowButtons.back' })}
        </Button>
        <span />
      </Stack>
    </Stack>
  );
};

export { IntegrityValidationStep };
